import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import JoditEditor from 'jodit-react';
import Select from 'react-select';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import PageHeader from '../../components/common/PageHeader';
import {
  useAddNotesMutation,
  useEditNotesMutation,
  useGetNoteListQuery
} from '../../redux/apis/files';
import { useIsLoggedInQuery } from '../../redux/apis/authApi';

import { BT_BUTTON_LABELS } from '../../utils/constant/buttons';
import { HOLIDAY_LOCALITY_OPTIONS, getRecordLocality } from '../../utils/constant/global';
import { PLACEHOLDER } from '../../utils/constant/placeHolders';
import { notification } from '../../utils/notification';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';

import '../Announcements/AnnouncementView.scss';

const { BTN_ADD_ANNOUNCEMENT, BTN_UPDATE_ANNOUNCEMENT, BTN_SAVE, BTN_UPDATE, BTN_CANCEL } =
  BT_BUTTON_LABELS;
const { PH_NATIONALITY } = PLACEHOLDER;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const AnnouncementFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { data: loginData } = useIsLoggedInQuery();
  const { data: noteListData, refetch } = useGetNoteListQuery({ skip: 0, limit: 1000 });
  const [addNote] = useAddNotesMutation();
  const [updateNote] = useEditNotesMutation();

  const [noteTitle, setNoteTitle] = useState('');
  const [description, setDescription] = useState('');
  const [locality, setLocality] = useState([]);
  const [loading, setLoading] = useState(false);

  const existingNote = useMemo(() => {
    if (location.state?.note) return location.state.note;
    if (!id) return null;
    return noteListData?.data?.find((note) => note._id === id) || null;
  }, [id, location.state?.note, noteListData?.data]);

  useEffect(() => {
    if (!isEdit || !existingNote) return;
    setNoteTitle(existingNote.title || '');
    setDescription(existingNote.description || '');
    setLocality(getRecordLocality(existingNote));
  }, [isEdit, existingNote]);

  const handleCancel = () => {
    navigate('/announcement');
  };

  const handleSave = async () => {
    if (!noteTitle.trim()) {
      notification('Please enter title', TOAST_ERROR);
      return;
    }

    if (!locality.length) {
      notification('Please select locality', TOAST_ERROR);
      return;
    }

    try {
      setLoading(true);

      if (isEdit) {
        const payload = {
          noteId: id,
          hrxNotes: {
            title: noteTitle.trim(),
            description,
            locality,
            createdBy: loginData?.data?._id
          }
        };
        const res = await updateNote(payload);
        notification(res?.data?.message, TOAST_SUCCESS);
      } else {
        const payload = {
          hrxNotes: {
            createdBy: loginData?.data?._id,
            title: noteTitle.trim(),
            description,
            locality
          }
        };
        const response = await addNote(payload);
        notification(response?.data?.message, TOAST_SUCCESS);
      }

      await refetch();
      navigate('/announcement');
    } catch (error) {
      notification(error.message || 'Failed to save announcement', TOAST_ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-xxl announcement-page">
      <PageHeader
        headerTitle={isEdit ? BTN_UPDATE_ANNOUNCEMENT : BTN_ADD_ANNOUNCEMENT}
        renderRight={() => (
          <Button variant="outline-secondary" onClick={handleCancel}>
            <i className="icofont-rounded-left me-1" aria-hidden />
            Back
          </Button>
        )}
      />

      <div className="announcement-form-page">
        <div className="announcement-form-page__header">
          <span className="announcement-form-page__icon" aria-hidden="true">
            <i className="icofont-bullhorn" />
          </span>
          <div className="announcement-form-page__copy">
            <h2 className="announcement-form-page__title">
              {isEdit ? BTN_UPDATE_ANNOUNCEMENT : BTN_ADD_ANNOUNCEMENT}
            </h2>
            <p className="announcement-form-page__subtitle">
              {isEdit
                ? 'Update this announcement for the selected locality'
                : 'Share an update with the selected locality'}
            </p>
          </div>
        </div>

        <div className="announcement-form-page__body">
          <div className="announcement-form-page__section">
            <div className="announcement-editor__field">
              <label className="announcement-form-page__label" htmlFor="announcement-title">
                Title
              </label>
              <input
                id="announcement-title"
                type="text"
                className="form-control announcement-form-page__input"
                placeholder="Enter title"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
              />
            </div>

            <div className="announcement-editor__field">
              <label className="announcement-form-page__label">Nationality</label>
              <Select
                className="announcement-form-page__select"
                classNamePrefix="announcement-form-select"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
                isMulti
                placeholder={PH_NATIONALITY}
                options={HOLIDAY_LOCALITY_OPTIONS}
                value={HOLIDAY_LOCALITY_OPTIONS.filter((opt) => locality.includes(opt.value))}
                onChange={(selected) =>
                  setLocality(selected ? selected.map((item) => item.value) : [])
                }
              />
            </div>

            <div className="announcement-editor__field">
              <label className="announcement-form-page__label">Description</label>
              <div className="announcement-editor__editor">
                <JoditEditor
                  style={{ minHeight: '250px' }}
                  value={description}
                  tabIndex={1}
                  onBlur={(content) => setDescription(content)}
                  className="joditAnnouncement"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="announcement-form-page__footer">
          <button
            type="button"
            className="announcement-form-page__cancel"
            onClick={handleCancel}
            disabled={loading}
          >
            {BTN_CANCEL}
          </button>
          <button
            type="button"
            className="announcement-form-page__save"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              'Saving...'
            ) : (
              <>
                <i className="icofont-check-circled" aria-hidden="true" />
                {isEdit ? BTN_UPDATE : BTN_SAVE}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementFormPage;
